import React, { FunctionComponent } from 'react';
import { SbBlokData } from '@storyblok/react';
import { graphql, useStaticQuery } from 'gatsby';

import { Avatar, Button, Col, Divider, Heading, P, Row } from '@savebetter/components';

import { MediaProps } from './Media';
import { PageSectionBlok, PageSectionProps } from './PageSection';
import { PageSectionHeader } from './PageSectionHeader';
import { Section } from './Section';

interface BlogPostPageProps {
  title: string;
  image: MediaProps[];
  body: SbBlokData[];
  intro?: string;
  categories?: { [key: string]: any }[];
}

interface BlogPostCustomData {
  title: string;
  description: string;
  media: MediaProps[];
}

interface Blok extends PageSectionBlok {
  sectionTitle: string;
  blogPosts: Array<string>;
  blogPostCustomData: BlogPostCustomData[];
}

interface HomePageBlogPostsProps extends PageSectionProps {
  blok: Blok;
}

interface AllBlogPostPages {
  allStoryblokEntry: {
    edges: [
      {
        node: {
          uuid: string;
          content: string;
          full_slug: string;
        };
      }
    ];
  };
}

export const HomePageBlogPosts: FunctionComponent<React.PropsWithChildren<HomePageBlogPostsProps>> = ({ blok }) => {
  const { allStoryblokEntry }: AllBlogPostPages = useStaticQuery(
    graphql`
      query blogPostQuery {
        allStoryblokEntry(filter: { field_component: { eq: "blogPostPage" } }) {
          edges {
            node {
              uuid
              content
              full_slug
            }
          }
        }
      }
    `
  );
  const { sectionTitle, blogPosts, hasBackground, hasBorder, blogPostCustomData, hasPaddingBottom, hasPaddingTop } =
    blok;
  const homeBlogPostsData = allStoryblokEntry.edges.filter(edge => blogPosts.includes(edge.node.uuid));
  const homeBlogPosts: BlogPostPageProps[] = homeBlogPostsData.map(homeBlogPostData =>
    JSON.parse(homeBlogPostData.node.content)
  );

  return (
    <Section
      width="md"
      hasBackground={hasBackground}
      hasPaddingBottom={hasPaddingBottom}
      hasPaddingTop={hasPaddingTop}
      hasBorder={hasBorder}
      blok={blok}
    >
      <PageSectionHeader className="md:pb-18 sm:text-center sm:pb-9">{sectionTitle}</PageSectionHeader>
      <Row gutter={{ sm: 6, md: 6, lg: 8 }}>
        {homeBlogPosts.map((blogPost, index) => {
          const { title, image, intro } = blogPost;
          return (
            <Col key={index} width={{ md: '1/3' }}>
              {image.length > 0 && (
                <a href={homeBlogPostsData[index].node.full_slug} target="_self">
                  <Avatar
                    src={
                      blogPostCustomData[index]?.media.length > 0
                        ? blogPostCustomData[index]?.media[0].img
                        : image[0].img
                    }
                    size="xl"
                    alt={
                      blogPostCustomData[index]?.media.length > 0
                        ? blogPostCustomData[index]?.media[0].alt
                        : image[0].alt
                    }
                    loading="lazy"
                    circle={false}
                    wrapperClassName="w-full"
                  />
                </a>
              )}
              <a href={homeBlogPostsData[index].node.full_slug} target="_self">
                <Heading kind="3" color="text-primary" className="my-7">
                  {blogPostCustomData[index]?.title ? blogPostCustomData[index]?.title : title}
                </Heading>
              </a>

              {intro && (
                <P>{blogPostCustomData[index]?.description ? blogPostCustomData[index]?.description : intro}</P>
              )}
              <div className="flex mt-7">
                <Button kind="primary" href={homeBlogPostsData[index].node.full_slug} target="_self">
                  Read full article
                </Button>
              </div>
              <Divider
                className={index === 2 ? 'hidden' : 'md:hidden mt-9 mb-3'}
                direction="horizontal"
                thickness="thin"
                size="full"
                color="border-black-60"
              />
            </Col>
          );
        })}
      </Row>
    </Section>
  );
};
